import React from 'react';
import clsx from 'clsx';
import { makeStyles } from '@material-ui/styles';
import Skeleton from 'react-loading-skeleton';
import { SkeletonCardFields } from 'components/Loading';
import {
  Card,
  CardHeader,
  CardContent,
  CardActions,
  Chip,
  Divider,
  Grid,
  Button,
  TextField,
  Typography
} from '@material-ui/core';
import ChipInput from 'material-ui-chip-input';
import { constants } from 'views/Campaign';

const useStyles = makeStyles(theme => ({
  root: {},
  cardHeader: {},
  radioLabel: {
    marginLeft: '0.7rem'
  },
  radioGroup: {
    marginLeft: '1rem'
  },
  cardActions: {
    flexDirection: 'row-reverse'
  },
  inputGrid: {
    //maxWidth: '40rem'
  },
  chipGrid: {
    maxWidth: '80%'
  },
  chipError: {
    backgroundColor: '#e53935',
    color: 'white',
    maxWidth: '80%'
  },
  textError: {
    color: theme.palette.error.main
  }
}));

const Details = props => {
  const { className, staticContext, campaign, next, showNav, ...rest } = props;
  const {
    goNext,
    thisCampaign,
    handleChange,
    onBlur,
    onFocus,
    shouldShowError,
    getErrorMessage,
    isLoading,
    isCampaignReadOnly
  } = campaign;
  const classes = useStyles();

  const { INTERNAL_SURVEY_RESPONSE } = constants.campaignOccurrenceTypes;

  const handleAddChip = (e, name) => {
    let current, previous;
    if (name === 'emailCampaignData.ccAddresses') {
      previous = thisCampaign?.emailCampaignData?.ccAddresses;
    } else if (name === 'emailCampaignData.bccAddresses') {
      previous = thisCampaign?.emailCampaignData?.bccAddresses;
    } else {
      previous = thisCampaign?.toAddresses;
    }
    current = previous?.length ? [...previous] : [];
    e.target.value && current.push(e.target.value);
    handleChange({ target: { name, value: current } });
  };

  const handleDeleteChip = (index, name) => {
    let current, previous;
    if (name === 'emailCampaignData.ccAddresses') {
      previous = thisCampaign?.emailCampaignData?.ccAddresses;
    } else if (name === 'emailCampaignData.bccAddresses') {
      previous = thisCampaign?.emailCampaignData?.bccAddresses;
    } else {
      previous = thisCampaign?.toAddresses;
    }
    current = [...previous];
    current.splice(index, 1);
    handleChange({ target: { name, value: current } });
  };

  const SkeletonCardContent = props => {
    return (
      <CardContent>
        <Grid container spacing={0}>
          <Grid item xs={4}>
            <Skeleton />
          </Grid>
          <SkeletonCardFields numberOfRows={5} />
        </Grid>
      </CardContent>
    );
  };

  if (
    thisCampaign.campaignOccurrenceType !== INTERNAL_SURVEY_RESPONSE &&
    thisCampaign.type === 'SMS'
  ) {
    return null;
  }

  return (
    <div className={classes.root}>
      <Card {...rest} className={clsx(classes.root, className)}>
        <form autoComplete="off">
          <CardHeader
            subheader="* indicates a required field"
            title={
              thisCampaign.type === 'EMAIL' ? 'Email Settings' : 'SMS Settings'
            }
            titleTypographyProps={{ variant: 'h1' }}
          />
          <Divider />
          {isLoading && <SkeletonCardContent />}
          {!isLoading && (
            <CardContent>
              <Grid container spacing={0}>
                {thisCampaign.type === 'EMAIL' && (
                  <>
                    <Grid item xs={12}>
                      <Grid item md={6} xs={12} className={classes.inputGrid}>
                        <TextField
                          fullWidth
                          label="From Name"
                          margin="dense"
                          name="emailCampaignData.fromName"
                          onChange={handleChange}
                          onFocus={onFocus}
                          onBlur={onBlur}
                          required
                          value={thisCampaign.emailCampaignData.fromName || ''}
                          variant="outlined"
                          error={shouldShowError('emailCampaignData.fromName')}
                          helperText={getErrorMessage(
                            'emailCampaignData.fromName'
                          )}
                          disabled={isCampaignReadOnly()}
                        />
                      </Grid>
                    </Grid>
                    <Grid item xs={12}>
                      <Grid item md={6} xs={12} className={classes.inputGrid}>
                        <TextField
                          fullWidth
                          label="From Address"
                          margin="dense"
                          name="emailCampaignData.fromAddress"
                          onChange={handleChange}
                          onFocus={onFocus}
                          onBlur={onBlur}
                          required
                          value={
                            thisCampaign.emailCampaignData.fromAddress || ''
                          }
                          variant="outlined"
                          error={shouldShowError(
                            'emailCampaignData.fromAddress'
                          )}
                          helperText={getErrorMessage(
                            'emailCampaignData.fromAddress'
                          )}
                          disabled={isCampaignReadOnly()}
                        />
                      </Grid>
                    </Grid>
                    <Grid item xs={12}>
                      <Grid item md={6} xs={12} className={classes.inputGrid}>
                        <TextField
                          fullWidth
                          label="Reply Address"
                          margin="dense"
                          name="emailCampaignData.replyAddress"
                          onChange={handleChange}
                          onFocus={onFocus}
                          onBlur={onBlur}
                          value={
                            thisCampaign.emailCampaignData.replyAddress || ''
                          }
                          required
                          variant="outlined"
                          error={shouldShowError(
                            'emailCampaignData.replyAddress'
                          )}
                          helperText={getErrorMessage(
                            'emailCampaignData.replyAddress'
                          )}
                          disabled={isCampaignReadOnly()}
                        />
                      </Grid>
                    </Grid>
                    <Grid item xs={12}>
                      {thisCampaign.campaignOccurrenceType ===
                        INTERNAL_SURVEY_RESPONSE && (
                        <Grid item md={6} xs={12} className={classes.inputGrid}>
                          <ChipInput
                            fullWidth
                            label="To"
                            margin="dense"
                            name="toAddresses"
                            disabled={isCampaignReadOnly()}
                            required
                            value={thisCampaign.toAddresses || []}
                            blurBehavior="add"
                            onBlur={chip => handleAddChip(chip, 'toAddresses')}
                            onDelete={(chip, index) =>
                              handleDeleteChip(index, 'toAddresses')
                            }
                            variant="outlined"
                            chipRenderer={(
                              { value, isFocused, handleDelete },
                              key
                            ) => (
                              <Chip
                                key={key}
                                onDelete={(chip, index) =>
                                  handleDelete(chip, index)
                                }
                                label={value}
                                disabled={isCampaignReadOnly()}
                                variant="outlined"
                                style={{ margin: '0 8px 8px 0' }}
                                className={
                                  !isFocused &&
                                  shouldShowError(`toAddresses.${key}`)
                                    ? classes.chipError
                                    : classes.chipGrid
                                }></Chip>
                            )}
                          />
                          {shouldShowError(`toAddresses`) && (
                            <Typography
                              className={classes.textError}
                              variant="caption"
                              display="block">
                              *Please add at least one recipient
                            </Typography>
                          )}
                        </Grid>
                      )}
                      <Grid item md={6} xs={12} className={classes.inputGrid}>
                        <ChipInput
                          fullWidth
                          label="Cc"
                          margin="dense"
                          name="emailCampaignData.ccAddresses"
                          value={
                            thisCampaign.emailCampaignData.ccAddresses || []
                          }
                          blurBehavior="add"
                          onBlur={chip =>
                            handleAddChip(chip, 'emailCampaignData.ccAddresses')
                          }
                          onDelete={(chip, index) =>
                            handleDeleteChip(
                              index,
                              'emailCampaignData.ccAddresses'
                            )
                          }
                          variant="outlined"
                          chipRenderer={(
                            { value, isFocused, handleDelete },
                            key
                          ) => (
                            <Chip
                              key={key}
                              onDelete={(chip, index) =>
                                handleDelete(chip, index)
                              }
                              label={value}
                              variant="outlined"
                              disabled={isCampaignReadOnly()}
                              style={{ margin: '0 8px 8px 0' }}
                              className={
                                !isFocused &&
                                shouldShowError(
                                  `emailCampaignData.ccAddresses.${key}`
                                )
                                  ? classes.chipError
                                  : classes.chipGrid
                              }></Chip>
                          )}
                          disabled={isCampaignReadOnly()}
                        />
                      </Grid>
                    </Grid>
                    <Grid item xs={12}>
                      <Grid item md={6} xs={12} className={classes.inputGrid}>
                        <ChipInput
                          fullWidth
                          label="Bcc"
                          margin="dense"
                          name="emailCampaignData.bccAddresses"
                          value={
                            thisCampaign.emailCampaignData.bccAddresses || []
                          }
                          blurBehavior="add"
                          onBlur={chip =>
                            handleAddChip(
                              chip,
                              'emailCampaignData.bccAddresses'
                            )
                          }
                          onDelete={(chip, index) =>
                            handleDeleteChip(
                              index,
                              'emailCampaignData.bccAddresses'
                            )
                          }
                          variant="outlined"
                          chipRenderer={(
                            { value, isFocused, handleDelete },
                            key
                          ) => (
                            <Chip
                              key={key}
                              onDelete={(chip, index) =>
                                handleDelete(chip, index)
                              }
                              label={value}
                              variant="outlined"
                              style={{ margin: '0 8px 8px 0' }}
                              disabled={isCampaignReadOnly()}
                              className={
                                !isFocused &&
                                shouldShowError(
                                  `emailCampaignData.bccAddresses.${key}`
                                )
                                  ? classes.chipError
                                  : classes.chipGrid
                              }></Chip>
                          )}
                          disabled={isCampaignReadOnly()}
                        />
                      </Grid>
                    </Grid>
                  </>
                )}
                {thisCampaign.type === 'SMS' && (
                  <>
                    <Grid item xs={12}>
                      <Grid item md={6} xs={12} className={classes.inputGrid}>
                        <ChipInput
                          fullWidth
                          label="To"
                          margin="dense"
                          name="toAddresses"
                          disabled={isCampaignReadOnly()}
                          required
                          value={thisCampaign.toAddresses || []}
                          blurBehavior="add"
                          onBlur={chip => handleAddChip(chip, 'toAddresses')}
                          onDelete={(chip, index) =>
                            handleDeleteChip(index, 'toAddresses')
                          }
                          variant="outlined"
                          chipRenderer={(
                            { value, isFocused, handleDelete },
                            key
                          ) => (
                            <Chip
                              key={key}
                              onDelete={(chip, index) =>
                                handleDelete(chip, index)
                              }
                              label={value}
                              variant="outlined"
                              style={{ margin: '0 8px 8px 0' }}
                              className={
                                !isFocused &&
                                shouldShowError(`toAddresses.${key}`)
                                  ? classes.chipError
                                  : classes.chipGrid
                              }></Chip>
                          )}
                        />
                        {shouldShowError(`toAddresses`) && (
                          <Typography
                            className={classes.textError}
                            variant="caption"
                            display="block">
                            *Please add at least one recipient
                          </Typography>
                        )}
                      </Grid>
                    </Grid>
                  </>
                )}
              </Grid>
            </CardContent>
          )}
          {(next || showNav) && (
            <>
              <Divider />
              <CardActions className={classes.cardActions}>
                <Button
                  color="primary"
                  variant="contained"
                  link={next}
                  onClick={goNext}>
                  Next
                </Button>
              </CardActions>
            </>
          )}
        </form>
      </Card>
    </div>
  );
};

export default Details;
